// ============================================================
// CLUBHUB – Beträge und Tage für Beiträge und Rechnungen
// ============================================================
// Die Ansichtstypen führen Beträge in EURO (number, wie
// `FeeTypeView.amount` im Kern). `euro()` ist `formatEuro` aus
// `lib/utils` (Euro, nicht Cent wie `formatCurrency`).
//
// Tage sind ISO-Text („2026-03-16“) und werden als UTC-Mitternacht
// gerechnet: so verschiebt keine Sommerzeit einen Fälligkeitstag.
// ============================================================
import { formatEuro } from "@/lib/utils";

/** 72 → „72,00 €“ */
export function euro(betrag: number): string {
  return formatEuro(betrag);
}

/** Gutschriften und Abzüge mit Minus: −72,00 € */
export function euroVorzeichen(betrag: number, negativ: boolean): string {
  return negativ ? `−${euro(Math.abs(betrag))}` : euro(betrag);
}

/** Auf Cent runden — Summen aus Kommazahlen laufen sonst auseinander. */
export function rund(betrag: number): number {
  return Math.round(betrag * 100) / 100;
}

export function summe<T>(liste: readonly T[], wert: (x: T) => number): number {
  return rund(liste.reduce((s, x) => s + wert(x), 0));
}

// ── Tage ────────────────────────────────────────────────────

const TAG_MS = 86_400_000;

export function tagAus(iso: string): Date {
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`);
}

export function isoAus(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Heute in München — nicht in der Zeitzone des Servers. */
export function heuteIso(): string {
  return new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Berlin" }).format(new Date());
}

/** Jetzt in München als „2026-10-23T19:30:00“. */
export function jetztIso(): string {
  const t = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date());
  return t.replace(" ", "T");
}

export function plusTage(iso: string, tage: number): string {
  return isoAus(new Date(tagAus(iso).getTime() + tage * TAG_MS));
}

/** Ganze Tage von `von` bis `bis` (positiv, wenn `bis` später liegt). */
export function tageZwischen(von: string, bis: string): number {
  return Math.round((tagAus(bis).getTime() - tagAus(von).getTime()) / TAG_MS);
}

const WOCHENTAG = new Intl.DateTimeFormat("de-DE", { weekday: "short", timeZone: "UTC" });
const MONAT_LANG = new Intl.DateTimeFormat("de-DE", { month: "long", timeZone: "UTC" });

/** „Mo“ */
export function wochentag(iso: string): string {
  return WOCHENTAG.format(tagAus(iso)).replace(".", "");
}

export function monatsname(monat: number): string {
  return MONAT_LANG.format(new Date(Date.UTC(2026, monat - 1, 1)));
}

/** „seit 12 Tagen“, „in 3 Tagen“, „heute“ */
export function tageText(tage: number): string {
  if (tage === 0) return "heute";
  const n = Math.abs(tage);
  const wort = n === 1 ? "Tag" : "Tagen";
  return tage < 0 ? `vor ${n} ${wort}` : `in ${n} ${wort}`;
}

/** Einzahl oder Mehrzahl mit Zahl: „1 Rechnung“, „3 Rechnungen“ */
export function anzahl(n: number, einzahl: string, mehrzahl: string): string {
  return `${new Intl.NumberFormat("de-DE").format(n)} ${n === 1 ? einzahl : mehrzahl}`;
}
